import { Trans, t } from '@lingui/macro';
import { PhoneCall } from 'lucide-react';
import { useState } from 'react';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { NotifyUsersOnNewVoiceCall } from '../../models/local/Types';
import { My } from '../../services/My';
import { twil } from '../../services/TwilioHelper';
import LimitedFeature from './LimitedFeature';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover"
import { Button } from "../../components/ui/button"
import { Checkbox } from "../../components/ui/checkbox"
import { Label } from "../../components/ui/label"
import { useToast } from "../../components/ui/use-toast"
import { cn } from "../../lib/utils"

const CallButton: React.FC<{ room: Room, isOngoing: boolean, call: RoomCall | undefined }> = ({ room, isOngoing, call }) => {
  const [inProgress, setInProgress] = useState(false);
  const { toast } = useToast();
  const alreadyConnected = twil.isConnected() && twil.getRoomId() == room.id;
  const meInTheCall = !!(call?.userIds.find(id => id === My.user.id))
  const [notifyMembers, setNotifyMembers] = useState(true);
  const [open, setOpen] = useState(false);

  function canConnect() {
    return !inProgress && !alreadyConnected && !isOngoing;
  }

  async function startCall(notify: NotifyUsersOnNewVoiceCall) {
    try {
      setInProgress(true);
      await twil.connect(room, My.user.id, notify);
    } catch (error) {
      console.error(error);
      const errName = (error as DOMException).name;
      let title: string;
      let description: string;
      if (errName === 'NotFoundError') {
        title = t`Microphone not found`;
        description = t`Please connect a microphone. If already connected, check that your browser has microphone access in system settings.`;
      } else if (errName === 'NotAllowedError') {
        title = t`Microphone permission denied`;
        description = t`Please allow microphone access in your browser settings and try again.`;
      } else {
        title = t`Could not connect to call`;
        description = (error as Error).message;
      }
      toast({ title, description, variant: "destructive" });
    } finally {
      setInProgress(false);
    }
  }

  function startNewCall() {
    setOpen(false);

    if (!canConnect())
      return;

    setTimeout(() => {
      if (notifyMembers) {
        startCall('notify-all-members')
      } else {
        startCall('notify-none')
      }
    }, 100);
  }

  const StartCallButton = () => (
    <Button
      className="w-full justify-center gap-2 font-semibold h-11 bg-primary hover:bg-primary/90 text-on-primary-text shadow-sm transition-all rounded-lg"
      onClick={startNewCall}
    >
      <PhoneCall className="w-4 h-4" />
      <Trans>Start a new voice call</Trans>
    </Button>
  );

  return (
    <>
      {canConnect() ? (
        <Popover open={open} onOpenChange={setOpen} modal>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10 data-[state=open]:bg-muted" data-cy="voice-call">
              <img
                src={`assets/icon/headset-mic.svg`}
                style={{ height: isOngoing ? "18px" : "24px" }}
                alt="Voice Call"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="end">
            <div className="flex flex-col gap-1 min-w-[200px]">
              {/* TODO: 통화 제한 복원 시 아래 주석 해제하고 StartCallButton 직접 호출 제거
              <LimitedFeature
                workspaceId={room.workspaceId}
                tier={'BASIC'}
                content={() => <StartCallButton />}
              />
              */}
              <StartCallButton />
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted rounded-sm select-none"
                onClick={() => setNotifyMembers(!notifyMembers)}
              >
                <Checkbox
                  id="notify-members"
                  checked={notifyMembers}
                  className="pointer-events-none"
                />
                <Label
                  className="text-sm font-medium leading-none cursor-pointer pointer-events-none"
                >
                  <Trans>Notify channel members</Trans>
                </Label>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </>
  );
}

export default CallButton;
